import { useAtom } from "jotai";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";

import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { HighlightPanel } from "@/components/Interface/HighlightPanel/HighlightPanel";
import {
  BusinessPersonalDetailsFormFields,
  excludedFieldsPrivate,
  PersonalDetailsFormFieldKeys,
  PersonalDetailsFormFields,
} from "@/components/Onboarding/Steps/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import { useUpdateCustomer } from "@/services/api";
import {
  BasicCustomerData,
  IdentificationStatusResponse,
  IdentificationStatusResponseKycStatus,
} from "@/services/model";
import { userAccountStateAtom } from "@/utils/atoms";
import {
  isBusinessCustomer,
  transformPersonalDetailsFormToBasicCustomerData,
} from "@/utils/customerUtils";
import { log } from "@/utils/log";

export const PersonalDataForm = ({
  localCustomerData,
  setLocalCustomerData,
  formData,
  identificationStatus,
  customer,
}: {
  localCustomerData?: BasicCustomerData;
  setLocalCustomerData: (data: BasicCustomerData) => void;
  formData: Omit<BusinessPersonalDetailsFormFields, "terms">;
  identificationStatus: IdentificationStatusResponse;
  customer: BasicCustomerData;
}) => {
  const [, setUserAccountState] = useAtom(userAccountStateAtom);
  const methods = useForm<BusinessPersonalDetailsFormFields>({
    mode: "onChange",
    defaultValues: formData,
  });
  const { mutateAsync: updateCustomer } = useUpdateCustomer();
  const { t, i18n } = useTranslation();
  const baseLanguage = i18n.language.split("-")[0];

  const saveData = (data: BusinessPersonalDetailsFormFields) => {
    if (localCustomerData?.birthday && data) {
      const postBody = transformPersonalDetailsFormToBasicCustomerData(
        data,
        localCustomerData,
        baseLanguage,
        t,
      );

      updateCustomer({
        data: postBody,
      })
        .then(() => {
          setLocalCustomerData(postBody);
          setUserAccountState((prev) => ({
            ...prev,
            editingPersonalData: false,
          }));
        })
        .catch((error) => {
          if (`${error?.response?.data}`.includes("invalid phone number")) {
            methods.setError("phone", {
              message: t("label.validation.phoneBackend"),
              type: "backendError",
            });
          } else {
            log("Error in customer form: " + error);
          }
        });
    }
  };

  // Use memoization to ensure that the array is only recreated when the array elements actually change.
  // This is to avoid it being a new array instance on every render, which would cause re-renderings.
  const excludedFields = useMemo(
    () => [
      // Business data is not changeable by the customer
      ...excludedFieldsPrivate,
      // Exclude date of birth field for business customers,
      ...(customer && isBusinessCustomer(customer)
        ? [PersonalDetailsFormFieldKeys.dateOfBirth]
        : []),
    ],
    [customer],
  );

  const readOnlyFields =
    identificationStatus?.kycStatus ===
    IdentificationStatusResponseKycStatus.KYC_data_mismatch
      ? [PersonalDetailsFormFieldKeys.formOfAddress]
      : [
          PersonalDetailsFormFieldKeys.formOfAddress,
          PersonalDetailsFormFieldKeys.firstName,
          PersonalDetailsFormFieldKeys.lastName,
          PersonalDetailsFormFieldKeys.dateOfBirth,
        ];

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(saveData)}>
        <div className="header-row mb-6">
          <h3 className="font-semibold text-secondary-100">
            {t("Onboarding.sections.personal-details.title")}
          </h3>
        </div>
        <PersonalDetailsFormFields
          excludedFields={excludedFields}
          readonlyFields={readOnlyFields}
        />
        <HighlightPanel className="mb-14 mt-14 max-w-[360px]">
          <p>
            <Trans
              components={[
                <a
                  key="0"
                  className="text-primary-100 underline"
                  href={t("translation:links.supportHomeLink")}
                  target="_blank"
                  rel="noreferrer"
                />,
              ]}
              i18nKey="Onboarding.sections.personal-details.disabledFieldsHint"
            />
          </p>
        </HighlightPanel>
        <div className="mt-10 flex flex-col justify-between gap-4 md:col-span-2 md:flex-row md:justify-end">
          <Button
            onClick={() => {
              methods.reset();
              setUserAccountState((prev) => ({
                ...prev,
                editingPersonalData: false,
              }));
            }}
            className="accent inverted w-full"
          >
            {t("common.buttons.cancel")}
          </Button>
          <SubmitButton
            className="accent w-full"
            label={t("common.buttons.save")}
          />
        </div>
      </form>
    </FormProvider>
  );
};
